/**
 * Composable for exporting design items to the legacy vector format
 * expected by the PHP veyop_add_to_cart() handler.
 *
 * Also provides print data extraction (colors, sizes) for the cart payload.
 */
import { useDesignStore, type ViewName } from '../stores/designStore';
import { useProductStore } from '../stores/productStore';
import { useConfigStore } from '../stores/configStore';
import { useSvgRenderer } from './useSvgRenderer';
import { useFontEmbedding } from './useFontEmbedding';
import type { DesignItem } from '@core/types';
import type { Pinia } from 'pinia';
import {
  buildSvgImageNode,
  isLayerVisible,
  normalizeImageTransform,
  resolveImageSourceUrl,
} from '@core/imagePipeline';
import { resolveItemSvgExporter, resolvePrintingMethodProviderId } from '@core/extensionRegistry';
import { clampEmbroiderySettings, embroideryViewProfile } from '@core/embroideryProfile';

export interface ResolveDesignerAssetUrlOpts {
  siteUrl?: string;
  veyopUrl?: string;
}

/**
 * Pure helper: make designer asset paths absolute (matches DesignView / cart export rules).
 * Use from canvas export without depending on Pinia where convenient.
 */
export function resolveDesignerAssetUrl(
  raw: string,
  opts: ResolveDesignerAssetUrlOpts = {},
): string {
  const src = (raw ?? '').trim();
  if (!src) return '';
  if (src.startsWith('data:') || src.startsWith('blob:')) return src;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith('//')) {
    const proto =
      typeof window !== 'undefined' && window.location?.protocol
        ? window.location.protocol
        : 'https:';
    return proto + src;
  }
  const veyopBase = (opts.veyopUrl || '').replace(/\/$/, '');
  if (src.startsWith('/')) {
    const siteTrim = (opts.siteUrl || '').trim();
    if (siteTrim !== '') {
      try {
        const base =
          typeof window !== 'undefined'
            ? new URL(siteTrim, window.location.href).href
            : siteTrim;
        const origin = new URL(base).origin;
        return origin + src;
      } catch {
        return siteTrim.replace(/\/$/, '') + src;
      }
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin + src;
    }
    return src;
  }
  if (veyopBase) {
    return `${veyopBase}/${src.replace(/^\//, '')}`;
  }
  return src;
}

export function useVectorExport(pinia?: Pinia | null) {
  const design = pinia ? useDesignStore(pinia) : useDesignStore();
  const product = pinia ? useProductStore(pinia) : useProductStore();

  /** Configured views plus any item-bearing view keys (addon views included). */
  function exportViewKeys(): ViewName[] {
    const keys = new Set<ViewName>(design.views);
    for (const [view, items] of Object.entries(design.items)) {
      if (Array.isArray(items) && items.length > 0) keys.add(view);
    }
    return [...keys];
  }
  const config = pinia ? useConfigStore(pinia) : useConfigStore();
  const { createTextSvg } = useSvgRenderer();
  const { getEmbeddableFontFaceCss } = useFontEmbedding(pinia);

  /** Make image URLs absolute (delegates to {@link resolveDesignerAssetUrl}). */
  function resolveAssetUrl(raw: string): string {
    return resolveDesignerAssetUrl(raw, {
      siteUrl: config.data.siteUrl,
      veyopUrl: config.data.veyopUrl,
    });
  }

  /**
   * Export all design items per view in legacy vector format.
   * Returns object like: { front: { 0: {...}, 1: {...} }, back: { ... } }
   * with numeric string keys (matching legacy jQuery-based exports.vector()).
   */
  async function exportVectors(): Promise<Record<string, Record<number, Record<string, unknown>>>> {
    const vectors: Record<string, Record<number, Record<string, unknown>>> = {};
    const views = exportViewKeys();

    for (const view of views) {
      const items = design.items[view];
      if (!items || items.length === 0) continue;

      const viewVectors: Record<number, Record<string, unknown>> = {};
      let idx = 0;

      const visibleItems = items.filter(isLayerVisible);
      const exported = await Promise.all(visibleItems.map((item) => exportItem(item, view)));
      for (const result of exported) {
        viewVectors[idx] = result;
        idx++;
      }

      vectors[view] = viewVectors;
    }

    return vectors;
  }

  /**
   * Convert a single DesignItem to legacy vector format.
   */
  async function exportItem(item: DesignItem, view: ViewName = design.activeView): Promise<Record<string, unknown>> {
    const result: Record<string, unknown> = {
      type: mapItemType(item),
      width: `${item.width}px`,
      height: `${item.height}px`,
      top: `${item.top}px`,
      left: `${item.left}px`,
      zIndex: String(item.zIndex),
      rotate: item.angle ?? 0,
      placementSpace: item.placementSpace ?? 'print-area',
      svg: await generateSvg(item),
    };

    if (item.type === 'text') {
      result.text = item.text ?? '';
      result.color = normalizeHex(String(item.color ?? '#000000'));
      result.fontFamily = String(item.fontFamily ?? 'Arial');
      result.align = String(item.textAlign ?? 'center');
      result.outlineC = String(item.outlineColor ?? '');
      result.outlineW = Number(item.outlineWidth ?? 0);
      if (item.fontWeight && item.fontWeight !== 'normal') {
        result.weight = String(item.fontWeight);
      }
      if (item.fontStyle && item.fontStyle !== 'normal') {
        result.Istyle = String(item.fontStyle);
      }
      if (item.textDecoration && item.textDecoration !== 'none') {
        result.decoration = String(item.textDecoration);
      }
    } else {
      // Carry the normalized image transform so the PHP print/download path can
      // reconstruct the source crop + adjustments without re-parsing the SVG.
      const transform = normalizeImageTransform(item);
      result.imageTransform = transform;
    }

    const viewProfile = embroideryViewProfile(config.data.items.embroideryProfile, view);
    const embroidery = clampEmbroiderySettings(item.embroidery, viewProfile);
    if (
      ['text', 'image', 'svg'].includes(item.type)
      && resolvePrintingMethodProviderId(product.selectedPrinting) === 'embroidery-pack'
      && viewProfile.enabled
    ) {
      result.veyop_embroidery = {
        version: 2,
        mode: 'global',
        threadColor: String(embroidery.threadColor ?? '#1a1a1a'),
        density: Number(embroidery.density),
        stitchAngle: Number(embroidery.stitchAngle),
        seed: String(embroidery.seed ?? item.id),
      };
    }

    if (isClipartItem(item)) {
      result.change_color = item.change_color ?? false;
      result.title = String(item.title ?? '');
      result.file_name = String(item.file_name ?? '');
      result.file = String(item.file ?? '');
      result.thumb = String(item.thumb ?? '');
      result.url = String(item.url ?? '');
      if (item.colors !== undefined) result.colors = item.colors;
      result.confirmColor = item.confirmColor ?? 0;
      if (item.clipart_id !== undefined) result.clipart_id = String(item.clipart_id);
      // Uploads: keep original URL server-side for PHP inlining. Library clipart: omit large (resolved by clipart_id + file_name on server).
      if (item.upload) {
        const ex = item as Record<string, unknown>;
        const largeRaw = String(ex.large ?? '').trim();
        if (largeRaw !== '') {
          result.large = resolveAssetUrl(largeRaw);
        }
      }
    }

    return result;
  }

  /**
   * Map Vue item type to legacy type name.
   */
  function mapItemType(item: DesignItem): string {
    if (item.type === 'text') return 'text';
    if (item.clipart_id) return 'clipart';
    if (item.upload) return 'upload';
    return item.type;
  }

  /**
   * Check if an item has clipart metadata.
   */
  function isClipartItem(item: DesignItem): boolean {
    return item.clipart_id !== undefined || item.file_name !== undefined;
  }

  /**
   * Generate SVG string for an item (matching legacy format).
   */
  async function generateSvg(item: DesignItem): Promise<string> {
    // Registered SVG exporters (addons) may override any item type's markup.
    const customExporter = resolveItemSvgExporter(item);
    if (customExporter) {
      await customExporter.prepare?.(item);
      const fontFaceCss = customExporter.needsFontFaceCss
        ? await getEmbeddableFontFaceCss(
            String(item.fontFamily ?? 'Arial'),
            String(item.fontWeight ?? 'normal'),
            String(item.fontStyle ?? 'normal'),
          )
        : '';
      return customExporter.exportSvg(item, { fontFaceCss });
    }

    if (item.type === 'text') {
      const fontFamily = String(item.fontFamily ?? 'Arial');
      const fontWeight = String(item.fontWeight ?? 'normal');
      const fontStyle = String(item.fontStyle ?? 'normal');
      const fontFaceCss = await getEmbeddableFontFaceCss(fontFamily, fontWeight, fontStyle);
      return createTextSvg({
        text: String(item.text ?? ''),
        fontFamily,
        fontSize: Number(item.fontSize ?? 24),
        fontWeight,
        fontStyle,
        color: String(item.color ?? '#000'),
        textAlign: String(item.textAlign ?? 'center'),
        outlineColor: String(
          item.outlineColor ??
            (item as Record<string, unknown>).outlineC ??
            'none',
        ),
        outlineWidth: Number(
          item.outlineWidth ?? (item as Record<string, unknown>).outlineW ?? 0,
        ),
        lineHeight: Number(item.lineHeight ?? 1.2),
        letterSpacing: Number(item.letterSpacing ?? 0),
        width: item.width,
        height: item.height,
      }, { fontFaceCss });
    }

    // Recolored SVG items must stay vectors in production/staff exports. Runtime
    // `src` is a blob URL and must never win over the actual markup here.
    if (item.type === 'svg' && typeof item.svg === 'string' && item.svg.includes('<svg')) {
      return item.svg;
    }

    // For image/svg items, wrap in SVG with pipeline clip/filter metadata
    const rasterRaw = resolveImageSourceUrl(item);
    if (rasterRaw) {
      const abs = resolveAssetUrl(rasterRaw);
      const clipId = `clip-${item.id}`;
      const filterId = `filter-${item.id}`;
      const { defs, body } = buildSvgImageNode({
        item,
        width: item.width,
        height: item.height,
        href: abs,
        clipId,
        filterId,
      });
      return [
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${item.width} ${item.height}" width="${item.width}" height="${item.height}">`,
        `<defs>${defs}</defs>`,
        body,
        `</svg>`,
      ].join('');
    }

    // If item already has an SVG string, use it directly
    if (typeof item.svg === 'string') return item.svg;

    return '';
  }

  /**
   * Extract print colors per view — collects unique hex values from items.
   */
  function extractPrintColors(): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    const views = exportViewKeys();

    for (const view of views) {
      const items = design.items[view];
      if (!items || items.length === 0) continue;

      const colors: string[] = [];
      for (const item of items) {
        if (item.type === 'text' && item.color) {
          const hex = normalizeHex(String(item.color));
          if (hex && !colors.includes(hex)) colors.push(hex);
        }
        // For clipart items with confirmed colors
        if (item.colors && typeof item.colors === 'object') {
          const itemColors = item.colors as Record<string, string>;
          for (const hex of Object.values(itemColors)) {
            const normalized = normalizeHex(String(hex));
            if (normalized && normalized !== 'none' && !colors.includes(normalized)) {
              colors.push(normalized);
            }
          }
        }
      }
      result[view] = colors;
    }

    return result;
  }

  /**
   * Extract print sizes per view — calculates bounding box of items
   * and converts to physical dimensions using product params.
   */
  function extractPrintSizes(): Record<string, Record<string, number>> {
    const sizes: Record<string, Record<string, number>> = {};
    const views = exportViewKeys();

    for (const view of views) {
      const items = design.items[view];
      if (!items || items.length === 0) continue;

      const areaStr = product.product?.areas[view];
      if (!areaStr) continue;

      let area: Record<string, unknown>;
      try {
        area = typeof areaStr === 'string' ? JSON.parse(areaStr.replace(/'/g, '"')) : areaStr;
      } catch {
        continue;
      }

      const areaW = parseFloat(String(area.width ?? 0));
      const areaH = parseFloat(String(area.height ?? 0));
      if (!areaW || !areaH) continue;

      // Find bounding box of all items
      let minTop = areaH, minLeft = areaW, maxRight = 0, maxBottom = 0;

      for (const item of items) {
        const iTop = Math.max(0, item.top);
        const iLeft = Math.max(0, item.left);
        const iRight = Math.min(areaW, item.left + item.width);
        const iBottom = Math.min(areaH, item.top + item.height);

        if (iTop < minTop) minTop = iTop;
        if (iLeft < minLeft) minLeft = iLeft;
        if (iRight > maxRight) maxRight = iRight;
        if (iBottom > maxBottom) maxBottom = iBottom;
      }

      const printW = maxRight - minLeft;
      const printH = maxBottom - minTop;
      if (printW <= 0 || printH <= 0) continue;

      // Convert pixel dimensions to physical using params
      const paramsStr = product.product?.params[view];
      if (!paramsStr) {
        sizes[view] = { width: printW, height: printH };
        continue;
      }

      let params: Record<string, unknown>;
      try {
        params = typeof paramsStr === 'string' ? JSON.parse(paramsStr.replace(/'/g, '"')) : paramsStr;
      } catch {
        sizes[view] = { width: printW, height: printH };
        continue;
      }

      const physicalW = parseFloat(String(params.width ?? areaW));
      const physicalH = parseFloat(String(params.height ?? areaH));

      const realW = (printW * physicalW) / areaW;
      const realH = (printH * physicalH) / areaH;

      sizes[view] = {
        width: Math.round(realW * 100) / 100,
        height: Math.round(realH * 100) / 100,
      };
    }

    return sizes;
  }

  /**
   * Collect all clipart IDs per view.
   */
  function extractCliparts(): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    const views = exportViewKeys();

    for (const view of views) {
      const items = design.items[view];
      if (!items || items.length === 0) continue;

      const ids: string[] = [];
      for (const item of items) {
        if (item.clipart_id) {
          ids.push(String(item.clipart_id));
        }
      }
      if (ids.length > 0) result[view] = ids;
    }

    return result;
  }

  /**
   * Collect all unique font families used across all views.
   */
  function extractFonts(): string {
    const fonts = new Set<string>();
    const views = exportViewKeys();

    for (const view of views) {
      const items = design.items[view];
      if (!items) continue;

      for (const item of items) {
        if (item.type === 'text' && item.fontFamily) {
          fonts.add(String(item.fontFamily));
        }
      }
    }

    return Array.from(fonts).join(',');
  }

  return {
    exportVectors,
    extractPrintColors,
    extractPrintSizes,
    extractCliparts,
    extractFonts,
    /** SVG string for one item — used by canvas export and cart PNGs */
    generateSvg,
    resolveAssetUrl,
  };
}

// ── Helpers ──────────────────────────────────────────────

function normalizeHex(hex: string): string {
  return hex.replace(/^#/, '').toLowerCase();
}
